import React, { Component } from "react";
import moment from "moment";
import { Link } from "react-router-dom";
import { getCookie, getStorage, getSelectedCycleId, setSelectedCycleId } from "../utils";
import CONSTANT from "../constant";
import { getAparDashboard, getEmployeePeriods, getAppraisalCycleList } from "../actions/AppraisalCycleAction";

class Home extends Component<any, any> {
  _isMounted = false;

  constructor(props: any) {
    super(props);
    const userDetail = getCookie(CONSTANT.cookie.userDetail);
    const permissionList = getStorage(CONSTANT.cookie.permissions) || [];
    this.state = {
      loading: true,
      userDetail: userDetail,
      activeCycle: null,
      hasAparHRAccess:
        permissionList.indexOf(CONSTANT.static.permissions.aparHRAccess) > -1,
      myStatus: null,
      managers: null,
      managerPendingCounts: null,
      cycleList: [],
      selectedCycleId: getSelectedCycleId() || "",
      periodList: [],
      selectedPeriod: null,
      periodsLoading: false,
    };
  }

  componentDidMount() {
    this._isMounted = true;
    this.init();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  // Load the cycles for the dashboard's cycle dropdown, then load the periods +
  // dashboard for the chosen cycle. With multiple cycles running in parallel the
  // employee picks which cycle to view here; switching cycles reloads periods.
  init = async () => {
    try {
      const res = await getAppraisalCycleList();
      if (!this._isMounted) return;
      const cycleList = (res?.result || []).filter((c: any) => {
        const s = (c.status || "").toLowerCase();
        return s === "active" || s === "draft";
      });

      // Default to the cycle selected in the topbar when it's one of the
      // available cycles, otherwise the first available cycle.
      let selectedCycleId = getSelectedCycleId();
      const hasSelected =
        selectedCycleId && cycleList.some((c: any) => String(c.id) === String(selectedCycleId));
      if (!hasSelected) {
        selectedCycleId = cycleList.length > 0 ? String(cycleList[0].id) : "";
        if (selectedCycleId) setSelectedCycleId(selectedCycleId);
      }

      this.setState({ cycleList, selectedCycleId });
      await this.fetchDashboardData(selectedCycleId);
    } catch (error) {
      if (!this._isMounted) return;
      console.error("Error loading dashboard cycles:", error);
      // Fall back to loading periods without a specific cycle.
      await this.fetchDashboardData("");
    }
  };

  fetchDashboardData = async (cycleId?: string) => {
    const employeeCode = this.state?.userDetail?.code || "";
    const rawCycleId = cycleId != null ? cycleId : this.state.selectedCycleId;
    // Send the cycle id as a number when numeric (the dropdown/localStorage hold
    // it as a string).
    const appraisalCycleId =
      rawCycleId && !Number.isNaN(Number(rawCycleId)) ? Number(rawCycleId) : rawCycleId;
    try {
      // Step 1: load the periods (incl. transfer periods) for the chosen cycle
      this.setState({ periodsLoading: true });
      const periodsRes = await getEmployeePeriods(
        appraisalCycleId ? { employeeCode, appraisalCycleId } : { employeeCode }
      );
      if (!this._isMounted) return;

      const periodList = periodsRes?.result || [];
      const selectedPeriod = this.pickDefaultPeriod(periodList);
      this.setState({ periodList, selectedPeriod, periodsLoading: false });

      // Step 2: load dashboard for that period within the selected cycle
      await this.loadDashboard(employeeCode, selectedPeriod, appraisalCycleId as any);
    } catch (error) {
      if (!this._isMounted) return;
      console.error("Error fetching dashboard data:", error);
      this.setState({ loading: false, periodsLoading: false });
    }
  };

  onCycleChange = (cycleId: string) => {
    // Keep the global selection in sync so other screens follow the same cycle.
    setSelectedCycleId(cycleId);
    this.setState({ selectedCycleId: cycleId });
    this.fetchDashboardData(cycleId);
  };

  loadDashboard = async (employeeCode: string, period: any, cycleId?: string) => {
    this.setState({ loading: true });
    // apar/dashboard resolves the cycle from appraisalCycleId (NOT from
    // periodId); without it the backend falls back to the latest cycle, which
    // shows the wrong managers/status. Always send the selected cycle.
    const rawCycleId = cycleId != null ? cycleId : this.state.selectedCycleId;
    const appraisalCycleId =
      rawCycleId && !Number.isNaN(Number(rawCycleId)) ? Number(rawCycleId) : rawCycleId;
    try {
      const res = await getAparDashboard(
        appraisalCycleId
          ? { employeeCode, periodId: period?.id || null, appraisalCycleId }
          : { employeeCode, periodId: period?.id || null }
      );
      if (!this._isMounted) return;
      if (res?.result) {
        this.setState({
          activeCycle: res.result.activeCycle || null,
          myStatus: res.result.myStatus || null,
          managers: res.result.managers || null,
          managerPendingCounts: res.result.managerPendingCounts || null,
          loading: false,
        });
      } else {
        this.setState({ loading: false });
      }
    } catch (error) {
      if (!this._isMounted) return;
      this.setState({ loading: false });
    }
  };

  onPeriodChange = (periodId: number) => {
    const { userDetail, periodList, selectedCycleId } = this.state;
    const employeeCode = userDetail?.code || "";
    const period = periodList.find((p: any) => p.id === periodId) || null;
    this.setState({ selectedPeriod: period });
    this.loadDashboard(employeeCode, period, selectedCycleId);
  };

  // A period may expose its cycle under several shapes depending on the backend
  // payload. Read defensively so the dashboard works regardless.
  getPeriodCycleId = (period: any): string => {
    const id =
      period?.appraisalCycleId ??
      period?.cycleId ??
      period?.cycle_id ??
      period?.cycle?.id ??
      null;
    return id != null ? String(id) : "";
  };

  getPeriodCycleName = (period: any): string => {
    return (
      period?.cycle_name ||
      period?.cycleName ||
      period?.cycle?.cycle_name ||
      period?.cycle?.financial_year ||
      ""
    );
  };

  // Prefer the latest period belonging to the cycle selected in the topbar.
  pickDefaultPeriod = (periodList: any[]) => {
    if (!periodList || periodList.length === 0) return null;
    const selectedCycleId = getSelectedCycleId();
    if (selectedCycleId) {
      const matches = periodList.filter(
        (p: any) => this.getPeriodCycleId(p) === String(selectedCycleId)
      );
      if (matches.length > 0) return matches[matches.length - 1];
    }
    return periodList[periodList.length - 1];
  };

  formatPeriodLabel = (period: any) => {
    const start = moment(period.startDate).format("DD/MM/YYYY");
    const end = period.endDate ? moment(period.endDate).format("DD/MM/YYYY") : "Present";
    const cycleName = this.getPeriodCycleName(period);
    return cycleName ? `${cycleName} — ${start} to ${end}` : `${start} to ${end}`;
  };

  getMyAparStage = () => {
    const { myStatus } = this.state;
    if (!myStatus) return "not_started";
    const { kpi_status, accepting_status, reviewing_status, reporting_status } = myStatus;
    if (accepting_status === "submitted") return "completed";
    if (accepting_status === "in_progress") return "accepting";
    if (reviewing_status === "submitted") return "accepting";
    if (reviewing_status === "in_progress") return "reviewing";
    if (reporting_status === "submitted") return "reviewing";
    if (reporting_status === "in_progress") return "reporting";
    if (kpi_status === "submitted" || kpi_status === "rm_approved" || kpi_status === "rvm_approved") return "reporting";
    if (kpi_status === "draft" || kpi_status === "sent_back_by_rm" || kpi_status === "sent_back_by_rvm") return "self_appraisal";
    return "not_started";
  };

  render() {
    const { loading, activeCycle, managers, managerPendingCounts, userDetail, cycleList, selectedCycleId, periodList, selectedPeriod, periodsLoading } = this.state;

    if (loading) {
      return (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading APAR Dashboard...</p>
        </div>
      );
    }

    const myAparStage = this.getMyAparStage();
    const stageOrder = ["not_started", "self_appraisal", "reporting", "reviewing", "accepting", "completed"];
    const currentStageIndex = stageOrder.indexOf(myAparStage);

    const reportingManager = managers?.reporting_manager_name || "";
    const reviewingManager = managers?.reviewing_manager_name || "";
    const acceptingManager = managers?.accepting_manager_name || "";
    const managersAssigned = reportingManager || reviewingManager || acceptingManager;

    const getStageStatus = (stageKey: string) => {
      const stageIdx = stageOrder.indexOf(stageKey);
      if (currentStageIndex > stageIdx) return "Completed";
      if (currentStageIndex === stageIdx) return "In Progress";
      return "Pending";
    };

    // Colors
    const textDark = "#2c3e50";
    const textMuted = "#7f8c8d";
    const cardShadow = "0 4px 14px rgba(0,0,0,0.08), 0 2px 6px rgba(0,0,0,0.04)";
    const cardBorder = "#e0e6ed";

    // Each status card has its own accent color
    const summaryCards = [
      { title: "SELF APPRAISAL", status: getStageStatus("self_appraisal"), faIcon: "fa-pencil-square-o", description: "KPI work plan & self assessment", url: CONSTANT.component.selfAppraisalList.url, accent: "#3b82f6", accentBg: "#eff6ff" },
      { title: "REPORTING MANAGER", status: getStageStatus("reporting"), faIcon: "fa-briefcase", description: "Assessment by reporting manager", url: CONSTANT.component.reportingManagerReport.url, accent: "#f59e0b", accentBg: "#fffbeb" },
      { title: "REVIEWING MANAGER", status: getStageStatus("reviewing"), faIcon: "fa-eye", description: "Review by reviewing manager", url: CONSTANT.component.reviewingManagerReport.url, accent: "#10b981", accentBg: "#ecfdf5" },
      { title: "ACCEPTANCE MANAGER", status: getStageStatus("accepting"), faIcon: "fa-check-square-o", description: "Final acceptance", url: CONSTANT.component.acceptingManagerReport.url, accent: "#8b5cf6", accentBg: "#f5f3ff" },
    ];

    const statusStyle = (status: string) => {
      if (status === "Completed") return { bg: "#ecfdf5", color: "#059669", border: "#a7f3d0" };
      if (status === "In Progress") return { bg: "#fffbeb", color: "#d97706", border: "#fde68a" };
      return { bg: "#f8fafc", color: "#94a3b8", border: "#e2e8f0" };
    };

    let daysRemaining = 0;
    let daysMessage = "";
    if (activeCycle) {
      const endDate = moment(activeCycle.effectiveTo);
      daysRemaining = Math.max(endDate.diff(moment(), "days"), 0);
      if (daysRemaining <= 0) daysMessage = "Cycle has ended";
      else if (daysRemaining <= 7) daysMessage = "Ending soon!";
      else daysMessage = "Days left in this cycle";
    }

    const sectionBox: React.CSSProperties = {
      background: "#fff",
      borderRadius: "10px",
      border: `1px solid ${cardBorder}`,
      boxShadow: cardShadow,
      padding: "22px",
    };

    const sectionTitle = (icon: string, iconColor: string, title: string, subtitle: string) => (
      <div style={{ marginBottom: "18px" }}>
        <h3 style={{ fontSize: "17px", fontWeight: 700, color: textDark, margin: 0 }}>
          <i className={`fa ${icon} me-2`} style={{ color: iconColor }}></i>{title}
        </h3>
        <p style={{ fontSize: "12px", color: textMuted, margin: "3px 0 0 26px" }}>{subtitle}</p>
      </div>
    );

    const rowItem: React.CSSProperties = { borderBottom: "1px solid #f1f5f9", padding: "13px 0" };
    const labelSt: React.CSSProperties = { fontSize: "11px", color: textMuted, textTransform: "uppercase", fontWeight: 600, letterSpacing: "0.5px", marginBottom: "1px" };
    const valueSt: React.CSSProperties = { fontSize: "15px", fontWeight: 600, color: textDark };

    const iconCircle = (bg: string, color: string): React.CSSProperties => ({
      width: "42px", height: "42px", minWidth: "42px",
      background: bg, color, fontSize: "16px", borderRadius: "50%",
      display: "inline-flex", alignItems: "center", justifyContent: "center",
    });

    // Timeline icons
    const timelineItems = [
      { label: "Start Date", value: moment(activeCycle?.effectiveFrom).format("DD MMM YYYY"), icon: "fa-calendar-check-o", bg: "#ecfdf5", color: "#059669" },
      { label: "End Date", value: moment(activeCycle?.effectiveTo).format("DD MMM YYYY"), icon: "fa-calendar-times-o", bg: "#fef2f2", color: "#dc2626" },
    ];

    // Manager items
    const managerItems = [
      { label: "Reporting Manager", name: reportingManager, icon: "fa-briefcase", bg: "#fffbeb", color: "#d97706" },
      { label: "Reviewing Manager", name: reviewingManager, icon: "fa-eye", bg: "#eff6ff", color: "#3b82f6" },
      { label: "Acceptance Manager", name: acceptingManager, icon: "fa-check-square", bg: "#f5f3ff", color: "#8b5cf6" },
    ];

    // Quick action items
    const quickActions = [
      { url: CONSTANT.component.selfAppraisalList.url, icon: "fa-pencil-square-o", color: "#3b82f6", bg: "#eff6ff", title: "SELF APPRAISAL", desc: "Fill your KPI work plan & self assessment", pending: 0 },
      { url: CONSTANT.component.reportingManagerReport.url, icon: "fa-users", color: "#f59e0b", bg: "#fffbeb", title: "REPORTING MANAGER", desc: "Assigned employees for reporting assessment", pending: managerPendingCounts?.reporting || 0 },
      { url: CONSTANT.component.reviewingManagerReport.url, icon: "fa-search", color: "#10b981", bg: "#ecfdf5", title: "REVIEWING MANAGER", desc: "Assigned employees for reviewing assessment", pending: managerPendingCounts?.reviewing || 0 },
      { url: CONSTANT.component.acceptingManagerReport.url, icon: "fa-gavel", color: "#8b5cf6", bg: "#f5f3ff", title: "ACCEPTING AUTHORITY", desc: "Assigned employees for final acceptance", pending: managerPendingCounts?.accepting || 0 },
    ];

    const totalManagerPending = (managerPendingCounts?.reporting || 0) + (managerPendingCounts?.reviewing || 0) + (managerPendingCounts?.accepting || 0);

    const pendingRoleCards = [
      { role: "Reporting Officer", count: managerPendingCounts?.reporting || 0, icon: "fa-briefcase", color: "#f59e0b", bg: "#fffbeb", border: "#fde68a", url: CONSTANT.component.reportingManagerReport.url },
      { role: "Reviewing Officer", count: managerPendingCounts?.reviewing || 0, icon: "fa-eye", color: "#10b981", bg: "#ecfdf5", border: "#a7f3d0", url: CONSTANT.component.reviewingManagerReport.url },
      { role: "Accepting Authority", count: managerPendingCounts?.accepting || 0, icon: "fa-gavel", color: "#8b5cf6", bg: "#f5f3ff", border: "#ddd6fe", url: CONSTANT.component.acceptingManagerReport.url },
    ];

    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <div className="eoffice-dashboard">
              <div className="card homedob p-4 mb-4">

                {/* ── Header ── */}
                <div style={{ marginBottom: "25px", borderBottom: "1px solid #e0e6ed", paddingBottom: "15px" }}>
                  <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                    <div>
                      <p className="time-range-label" style={{ margin: 0, fontSize: "15px", color: textMuted, fontWeight: 500 }}>
                        Welcome back,
                      </p>
                      <h2 style={{
                        margin: "2px 0 0 0",
                        fontSize: "22px",
                        fontWeight: 800,
                        background: "linear-gradient(135deg, #1e40af 0%, #3b82f6 60%, #6366f1 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                        letterSpacing: "0.3px",
                        lineHeight: 1.2,
                      }}>
                        {userDetail?.name || "User"}
                      </h2>
                      {userDetail?.designation && (
                        <span style={{
                          display: "inline-block",
                          marginTop: "5px",
                          fontSize: "11px",
                          fontWeight: 600,
                          color: "#6366f1",
                          background: "#eef2ff",
                          border: "1px solid #c7d2fe",
                          borderRadius: "20px",
                          padding: "2px 10px",
                          letterSpacing: "0.4px",
                          textTransform: "uppercase",
                        }}>
                          {userDetail.designation}
                        </span>
                      )}
                    </div>

                    {/* ── Cycle + Period Filters ── */}
                    <div className="d-flex gap-3 flex-wrap">
                      {/* Cycle dropdown */}
                      {cycleList.length > 0 && (
                        <div style={{ minWidth: "220px" }}>
                          <label style={{ fontSize: "11px", fontWeight: 600, color: textMuted, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: "4px" }}>
                            <i className="fa fa-refresh me-1" style={{ color: "#8b5cf6" }}></i> Appraisal Cycle
                          </label>
                          <select
                            className="form-control form-control-sm"
                            style={{ fontSize: "13px", fontWeight: 500, borderRadius: "6px", border: "1px solid #d1d5db" }}
                            value={selectedCycleId || ""}
                            onChange={(e) => this.onCycleChange(e.target.value)}
                          >
                            {cycleList.map((cycle: any) => (
                              <option key={cycle.id} value={cycle.id}>
                                {cycle.cycle_name || cycle.cycleName || cycle.financial_year || `Cycle #${cycle.id}`}
                                {cycle.status ? ` (${cycle.status})` : ""}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Period dropdown (includes transfer periods) */}
                      {periodList.length > 0 && (
                        <div style={{ minWidth: "260px" }}>
                          <label style={{ fontSize: "11px", fontWeight: 600, color: textMuted, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: "4px" }}>
                            <i className="fa fa-calendar me-1" style={{ color: "#3b82f6" }}></i> Period
                          </label>
                          {periodsLoading ? (
                            <div style={{ fontSize: "13px", color: textMuted, padding: "6px 0" }}>
                              <i className="fa fa-spinner fa-spin me-1"></i> Loading...
                            </div>
                          ) : (
                            <select
                              className="form-control form-control-sm"
                              style={{ fontSize: "13px", fontWeight: 500, borderRadius: "6px", border: "1px solid #d1d5db" }}
                              value={selectedPeriod?.id || ""}
                              onChange={(e) => this.onPeriodChange(Number(e.target.value))}
                            >
                              {periodList.map((period: any) => (
                                <option key={period.id} value={period.id}>
                                  {this.formatPeriodLabel(period)}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* ── My APAR Status ── */}
                {sectionTitle("fa-bar-chart", "#3b82f6", "My APAR Status", "Your current appraisal progress at each stage")}
                <div className="summary-grid" style={{ marginBottom: "28px" }}>
                  {summaryCards.map((card, index) => {
                    const ss = statusStyle(card.status);
                    return (
                      <div
                        key={index}
                        className="summary-card"
                        style={{
                          border: `1px solid ${cardBorder}`,
                          borderLeft: `5px solid ${card.accent}`,
                          boxShadow: cardShadow,
                          borderRadius: "10px",
                          position: "relative",
                          overflow: "hidden",
                          cursor: "default",
                        }}
                      >
                        {/* Status ribbon */}
                        <div style={{
                          position: "absolute", top: 0, right: 0,
                          background: ss.bg, color: ss.color,
                          fontSize: "10px", fontWeight: 700, padding: "5px 14px",
                          borderBottomLeftRadius: "10px", border: `1px solid ${ss.border}`,
                          textTransform: "uppercase", letterSpacing: "0.5px",
                        }}>
                          {card.status === "Completed" && <i className="fa fa-check me-1"></i>}
                          {card.status === "In Progress" && <i className="fa fa-spinner fa-pulse me-1"></i>}
                          {card.status}
                        </div>
                        <div className="card-icon">
                          <i className={`fa ${card.faIcon}`} style={{ fontSize: "52px", color: card.accent }}></i>
                        </div>
                        <div className="card-content">
                          <div className="card-title" style={{ fontWeight: 600, color: textDark }}>{card.title}</div>
                          <div className="card-description">{card.description}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {activeCycle && (
                  <>
                    {/* ── Cycle Timeline + Managers ── */}
                    <div className="charts-section" style={{ marginBottom: "28px" }}>

                      {/* Left: Timeline */}
                      <div style={sectionBox}>
                        {sectionTitle("fa-calendar", "#f59e0b", "Cycle Timeline", "Appraisal period details")}
                        <div>
                          {timelineItems.map((item, idx) => (
                            <div key={idx} className="d-flex align-items-center gap-3" style={rowItem}>
                              <span style={iconCircle(item.bg, item.color)}><i className={`fa ${item.icon}`}></i></span>
                              <div style={{ flex: 1 }}>
                                <div style={labelSt}>{item.label}</div>
                                <div style={valueSt}>{item.value}</div>
                              </div>
                            </div>
                          ))}
                          <div className="d-flex align-items-center gap-3" style={{ ...rowItem, borderBottom: "none" }}>
                            <span style={iconCircle("#fff7ed", "#ea580c")}><i className="fa fa-hourglass-half"></i></span>
                            <div style={{ flex: 1 }}>
                              <div style={labelSt}>Days Remaining</div>
                              <div style={{ ...valueSt, color: daysRemaining <= 7 ? "#dc2626" : textDark }}>
                                {daysRemaining} days
                                <span style={{ fontSize: "12px", color: textMuted, marginLeft: "6px", fontWeight: 400 }}>({daysMessage})</span>
                              </div>
                            </div>
                            {daysRemaining > 0 && daysRemaining <= 30 && (
                              <span style={{
                                background: daysRemaining <= 7 ? "#fef2f2" : "#fffbeb",
                                color: daysRemaining <= 7 ? "#dc2626" : "#d97706",
                                fontSize: "11px", fontWeight: 700, padding: "4px 12px", borderRadius: "20px",
                                border: `1px solid ${daysRemaining <= 7 ? "#fecaca" : "#fde68a"}`,
                              }}>
                                {daysRemaining <= 7 ? "Urgent" : "Act Soon"}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Right: Managers */}
                      <div style={sectionBox}>
                        {sectionTitle("fa-user-circle", "#8b5cf6", "My Managers", managersAssigned ? "Assigned authorities for this cycle" : "No managers assigned")}
                        {managersAssigned ? (
                          <div>
                            {managerItems.map((mgr, idx, arr) => (
                              <div key={idx} className="d-flex align-items-center gap-3" style={idx === arr.length - 1 ? { ...rowItem, borderBottom: "none" } : rowItem}>
                                <span style={iconCircle(mgr.bg, mgr.color)}><i className={`fa ${mgr.icon}`}></i></span>
                                <div style={{ flex: 1 }}>
                                  <div style={labelSt}>{mgr.label}</div>
                                  <div style={valueSt}>{mgr.name || <span style={{ color: "#cbd5e1", fontWeight: 400 }}>Not Assigned</span>}</div>
                                </div>
                                {mgr.name ? (
                                  <span style={{ background: "#ecfdf5", color: "#059669", fontSize: "10px", fontWeight: 700, padding: "3px 10px", borderRadius: "20px", border: "1px solid #a7f3d0" }}>Assigned</span>
                                ) : (
                                  <span style={{ background: "#f8fafc", color: "#94a3b8", fontSize: "10px", fontWeight: 700, padding: "3px 10px", borderRadius: "20px", border: "1px solid #e2e8f0" }}>Pending</span>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="d-flex flex-column align-items-center justify-content-center" style={{ padding: "35px 20px" }}>
                            <span style={{ ...iconCircle("#fffbeb", "#f59e0b"), width: "56px", height: "56px", minWidth: "56px", fontSize: "22px", marginBottom: "14px" }}>
                              <i className="fa fa-exclamation-triangle"></i>
                            </span>
                            <h5 style={{ color: textDark, marginBottom: "6px", fontWeight: 600, fontSize: "16px" }}>No Managers Assigned</h5>
                            <p style={{ color: textMuted, textAlign: "center", fontSize: "13px", margin: 0, maxWidth: "280px" }}>
                              Please contact HR to get your managers assigned for this appraisal cycle.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* ── Pending Actions as Manager ── */}
                    {managerPendingCounts && (
                      <div style={{ marginBottom: "28px" }}>
                        <div style={{ marginBottom: "18px" }}>
                          <h3 style={{ fontSize: "17px", fontWeight: 700, color: textDark, margin: 0 }}>
                            <i className="fa fa-clock-o me-2" style={{ color: "#dc2626" }}></i>
                            Pending Actions as Manager
                            {totalManagerPending > 0 && (
                              <span style={{
                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                background: "#dc2626", color: "#fff",
                                fontSize: "11px", fontWeight: 700,
                                width: "22px", height: "22px", borderRadius: "50%",
                                marginLeft: "8px", verticalAlign: "middle",
                              }}>{totalManagerPending}</span>
                            )}
                          </h3>
                          <p style={{ fontSize: "12px", color: textMuted, margin: "3px 0 0 26px" }}>
                            Employees waiting for your action across your roles
                          </p>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px" }}>
                          {pendingRoleCards.map((card, idx) => (
                            <Link
                              key={idx}
                              to={card.url}
                              className="text-decoration-none"
                              style={{
                                display: "flex", alignItems: "center", gap: "16px",
                                background: card.count > 0 ? card.bg : "#f8fafc",
                                border: `1px solid ${card.count > 0 ? card.border : "#e2e8f0"}`,
                                borderLeft: `5px solid ${card.count > 0 ? card.color : "#cbd5e1"}`,
                                borderRadius: "10px", padding: "18px 20px",
                                boxShadow: cardShadow, transition: "box-shadow 0.2s",
                              }}
                            >
                              <span style={{
                                width: "48px", height: "48px", minWidth: "48px", borderRadius: "50%",
                                background: card.count > 0 ? card.color : "#cbd5e1",
                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                color: "#fff", fontSize: "20px",
                              }}>
                                <i className={`fa ${card.icon}`}></i>
                              </span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: "11px", color: textMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px" }}>
                                  {card.role}
                                </div>
                                <div style={{ fontSize: "26px", fontWeight: 800, color: card.count > 0 ? card.color : "#94a3b8", lineHeight: 1 }}>
                                  {card.count}
                                </div>
                                <div style={{ fontSize: "12px", color: textMuted, marginTop: "3px" }}>
                                  {card.count === 0 ? "No pending items" : card.count === 1 ? "employee pending" : "employees pending"}
                                </div>
                              </div>
                              {card.count > 0 && (
                                <i className="fa fa-arrow-right" style={{ color: card.color, fontSize: "14px" }}></i>
                              )}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* ── Quick Actions ── */}
                    {sectionTitle("fa-bolt", "#dc2626", "Quick Actions", "Navigate to different APAR forms")}
                    <div className="summary-grid" style={{ marginBottom: "10px" }}>
                      {quickActions.map((action, idx) => (
                        <Link
                          key={idx}
                          to={action.url}
                          className="summary-card text-decoration-none"
                          style={{
                            border: `1px solid ${cardBorder}`,
                            borderBottom: `4px solid ${action.color}`,
                            boxShadow: cardShadow,
                            borderRadius: "10px",
                            position: "relative",
                          }}
                        >
                          {action.pending > 0 && (
                            <span style={{
                              position: "absolute", top: "10px", right: "10px",
                              background: "#dc2626", color: "#fff",
                              fontSize: "11px", fontWeight: 700,
                              padding: "2px 8px", borderRadius: "12px",
                              lineHeight: "18px",
                            }}>
                              {action.pending} pending
                            </span>
                          )}
                          <div className="card-icon">
                            <i className={`fa ${action.icon}`} style={{ fontSize: "48px", color: action.color }}></i>
                          </div>
                          <div className="card-content">
                            <div className="card-title" style={{ fontWeight: 600, color: textDark }}>{action.title}</div>
                            <div className="card-description">{action.desc}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default Home;
